/* @flow */

import clsx from 'clsx';
import * as React from 'react';

import Localizer from '../../../helpers/localization';
import { PictoArrowDown, PictoArrowUp } from '../../pictos/Element';
import './SortAndFilter.css';
import { type SortAndFilterType, SortDirection, SortKind } from './type';

const availableSorts = [SortKind.Default, SortKind.Chronological];

const getSortName = (kind: SortKind): string => {
  switch (kind) {
    case SortKind.Default:
      return Localizer.localize('grid.sort_and_filter.kind.default');
    case SortKind.Chronological:
      return Localizer.localize('grid.sort_and_filter.kind.date');

    // No default
  }
};

type SortAndFilterPropType = {
  +updateSortAndFilter: (sortAndFilter: SortAndFilterType) => void,
};

const SortAndFilter = ({ updateSortAndFilter }: SortAndFilterPropType): React.Node => {
  const [sortKind, setSortKind] = React.useState(SortKind.Default);
  const [sortDirection, setSortDirection] = React.useState(SortDirection.Ascendant);
  const [filter, setFilter] = React.useState('');

  React.useEffect(() => {
    updateSortAndFilter({
      direction: sortDirection,
      filter,
      kind: sortKind,
    });
  }, [filter, sortDirection, sortKind, updateSortAndFilter]);

  const handleFilterOnChange = React.useCallback((event: SyntheticInputEvent<HTMLInputElement>) => {
    setFilter(event.currentTarget.value);
  }, []);

  const handleSortOnClick = React.useCallback(
    (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement>) => {
      const kind = SortKind.cast(event.currentTarget.getAttribute('data-kind'));
      if (!kind) {
        // Invalid value (shouldn't occur
        return;
      }

      if (kind === sortKind && kind !== SortKind.Default) {
        // Kind already selected: revert direction
        setSortDirection(sortDirection === SortDirection.Ascendant ? SortDirection.Descendant : SortDirection.Ascendant);
      } else {
        // New sort kind
        setSortKind(kind);
      }
    },
    [sortDirection, sortKind],
  );

  const renderDirection = (kind: SortKind): React.Node => {
    if (kind !== sortKind || kind === SortKind.Default) {
      return null;
    }

    return sortDirection === SortDirection.Ascendant ? <PictoArrowUp forceHoverEffect /> : <PictoArrowDown forceHoverEffect />;
  };

  return (
    <div className='sortAndFilter'>
      <input onChange={handleFilterOnChange} placeholder={Localizer.localize('grid.sort_and_filter.placeholder')} type='text' value={filter} />
      {availableSorts.map((kind) => {
        const sortName = getSortName(kind);

        return (
          <button
            aria-label={Localizer.localize('accessibility.navigation.sort_by_kind', { name: sortName })}
            className={clsx(sortKind === kind && 'selected')}
            data-kind={kind as string}
            key={kind as string}
            onClick={handleSortOnClick}
            type='button'
          >
            {sortName} {renderDirection(kind)}
          </button>
        );
      })}
    </div>
  );
};

export default SortAndFilter;
